import { createContext, useContext, useReducer, useEffect, useState, ReactNode } from 'react';
import { authAPI } from '../services/api/authAPI';
import { questionnaireAPI } from '../services/api/questionnaireAPI';
import { User, AuthState, LoginCredentials, RegisterData } from '../types/auth';
import apiClient from '../services/api/config';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { clearCompanyUsersCache } from '../hooks/useCompanyUsers';
import { pmTemplatesAPI } from '../services/api/pmTemplatesAPI';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Alert,
} from '@mui/material';
import { GlassSpinner } from '../components/common/GlassLoader';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  needsQuestionnaire: boolean;
  checkQuestionnaireStatus: () => Promise<boolean>;
  setQuestionnaireCompleted: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_QUESTIONNAIRE_STATUS'; payload: boolean };

const initialState: AuthState & { needsQuestionnaire: boolean } = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  isLoading: true,
  error: null,
  needsQuestionnaire: false,
};

// R2-backed avatars are stored as relative "/r2/..." paths that 404 on the
// frontend origin. Resolve the avatar to a browser-loadable URL once, here,
// and mirror it across the three field names different components read
// (`avatar`, `avatarUrl`, `profilePic`) so every render site just works.
function normalizeUser(user: any): any {
  if (!user) return user;
  const raw = user.avatarUrl || user.profilePic || user.avatar;
  if (!raw) return user;
  const resolved = resolveAssetUrl(raw);
  return { ...user, avatar: resolved, avatarUrl: resolved, profilePic: resolved };
}

function authReducer(state: AuthState & { needsQuestionnaire: boolean }, action: AuthAction): AuthState & { needsQuestionnaire: boolean } {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: normalizeUser(action.payload.user),
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        needsQuestionnaire: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        needsQuestionnaire: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: normalizeUser(action.payload),
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SET_QUESTIONNAIRE_STATUS':
      return {
        ...state,
        needsQuestionnaire: action.payload,
      };
    default:
      return state;
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if user is authenticated on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const user = await authAPI.getProfile();
          dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
          
          try {
            const status = await questionnaireAPI.getQuestionnaireStatus();
            dispatch({ type: 'SET_QUESTIONNAIRE_STATUS', payload: !status.completed });
          } catch (error) {
            console.warn('Could not fetch questionnaire status:', error);
            dispatch({ type: 'SET_QUESTIONNAIRE_STATUS', payload: false });
          }
        } catch (error) {
          localStorage.removeItem('token');
          dispatch({ type: 'LOGOUT' });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const data = await authAPI.login(credentials);

      if (data.requiresCompanySelection && data.companyChoices) {
        const err = new Error('COMPANY_SELECTION_REQUIRED') as any;
        err.companyChoices = data.companyChoices;
        err.email = data.email;
        throw err;
      }

      dispatch({ type: 'LOGIN_START' });

      const user = data.user;
      
      const token = data.token ?? data.tokens?.accessToken;
      if (!token) {
        throw new Error('Login response missing token');
      }
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      if (data.tokens?.refreshToken) {
        localStorage.setItem('refreshToken', data.tokens.refreshToken);
      }
      
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
      window.dispatchEvent(new Event('auth-login'));
      
      try {
        const status = await questionnaireAPI.getQuestionnaireStatus();
        dispatch({ type: 'SET_QUESTIONNAIRE_STATUS', payload: !status.completed });
      } catch (error) {
        console.warn('Could not fetch questionnaire status:', error);
        dispatch({ type: 'SET_QUESTIONNAIRE_STATUS', payload: false });
      }
    } catch (error: any) {
      if (error.message === 'COMPANY_SELECTION_REQUIRED') {
        throw error;
      }
      const errorMessage = error.response?.data?.errorMessage || error.response?.data?.message || error.response?.data?.mesaira || error.message || 'Login failed';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      // Re-throw with the human-readable message so the login page shows
      // "Invalid email or password" instead of the raw axios "Request
      // failed with status code 401". Preserve known extra fields.
      const friendlyError = new Error(errorMessage) as any;
      friendlyError.response = error.response;
      throw friendlyError;
    }
  };

  const register = async (data: RegisterData) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const responseData = await authAPI.register(data);
      const user = responseData.user;
      
      const token = responseData.token ?? responseData.tokens?.accessToken;
      if (!token) {
        throw new Error('Registration response missing token');
      }
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      if (responseData.tokens?.refreshToken) {
        localStorage.setItem('refreshToken', responseData.tokens.refreshToken);
      }
      
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
    } catch (error: any) {
      const errorMessage = error.response?.data?.errorMessage || error.response?.data?.message || error.response?.data?.mesaira || error.message || 'Registration failed';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    // Drop any cross-tenant caches so the next login doesn't read stale
    // data from the previous user's company.
    clearCompanyUsersCache();
    pmTemplatesAPI.clearCache();
    dispatch({ type: 'LOGOUT' });
    window.location.href = '/login';
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      
      const response = await authAPI.refreshToken(refreshToken);
      const { accessToken } = response;
      
      localStorage.setItem('token', accessToken);
      // Note: We don't dispatch here as the token is updated but user data remains the same
    } catch (error) {
      logout();
      throw error;
    }
  };

  const updateProfile = async (data: Partial<User>) => {
    try {
      const updatedUser = await authAPI.updateProfile(data);
      dispatch({ type: 'UPDATE_USER', payload: updatedUser });
      // Keep localStorage in sync so a reload before the next /auth/me
      // fetch doesn't flash the stale avatar/name.
      try { localStorage.setItem('user', JSON.stringify(updatedUser)); } catch (_e) { /* quota */ }
    } catch (error) {
      throw error;
    }
  };

  const checkQuestionnaireStatus = async (): Promise<boolean> => {
    try {
      const status = await questionnaireAPI.getQuestionnaireStatus();
      const needsQuestionnaire = !status.completed;
      dispatch({ type: 'SET_QUESTIONNAIRE_STATUS', payload: needsQuestionnaire });
      return needsQuestionnaire;
    } catch (error) {
      console.error('Error checking questionnaire status:', error);
      return false;
    }
  };

  const setQuestionnaireCompleted = () => {
    dispatch({ type: 'SET_QUESTIONNAIRE_STATUS', payload: false });
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    refreshToken,
    updateProfile,
    needsQuestionnaire: state.needsQuestionnaire,
    checkQuestionnaireStatus,
    setQuestionnaireCompleted,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <ForcePasswordChangeDialog
        open={state.isAuthenticated && !!state.user?.mustChangePassword}
        onPasswordChanged={() => {
          if (state.user) {
            dispatch({ type: 'UPDATE_USER', payload: { ...state.user, mustChangePassword: false } });
          }
        }}
      />
    </AuthContext.Provider>
  );
}

function ForcePasswordChangeDialog({ open, onPasswordChanged }: { open: boolean; onPasswordChanged: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    setError('');
    if (!currentPassword) {
      setError('Current password is required');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    try {
      setLoading(true);
      await apiClient.post('/auth/change-password', { currentPassword, newPassword });
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        onPasswordChanged();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.7)', pointerEvents: 'auto' } } }}
      sx={{ zIndex: 2000, '& .MuiDialog-paper': { pointerEvents: 'auto' }, pointerEvents: 'auto' }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Change Your Password</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="warning" sx={{ borderRadius: '8px' }}>
            For security, you must set a new password before continuing.
          </Alert>
          {error && <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ borderRadius: '8px' }}>Password changed! Redirecting...</Alert>}
          <TextField
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            fullWidth
            autoFocus
            disabled={loading || success}
            inputProps={{ autoComplete: 'current-password' }}
            InputProps={{ sx: { pointerEvents: 'auto' } }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (document.querySelector('[data-testid="new-password-field"] input') as HTMLInputElement)?.focus(); } }}
          />
          <TextField
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            disabled={loading || success}
            helperText="Minimum 8 characters"
            inputProps={{ autoComplete: 'new-password', 'data-testid': 'new-password-input' }}
            InputProps={{ sx: { pointerEvents: 'auto' } }}
            data-testid="new-password-field"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (document.querySelector('[data-testid="confirm-password-field"] input') as HTMLInputElement)?.focus(); } }}
          />
          <TextField
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            disabled={loading || success}
            inputProps={{ autoComplete: 'new-password', 'data-testid': 'confirm-password-input' }}
            InputProps={{ sx: { pointerEvents: 'auto' } }}
            data-testid="confirm-password-field"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (!loading && !success) handleSubmit(); } }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || success || !currentPassword || !newPassword || !confirmPassword}
          startIcon={loading ? <GlassSpinner size={20} /> : undefined}
          sx={{ bgcolor: '#4B88FF', '&:hover': { bgcolor: '#2563EB' } }}
        >
          {loading ? 'Changing...' : 'Set New Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
